import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Input } from '@connected/ui/components/input';
import { Page, Section } from '@connected/ui/features/layouts';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { DistributionBoardCreateModal, DistributionBoardTable } from '@/components/features/distribution-board-list';
import { Loading } from '@/components/loading';
import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { useUrlSearchSync } from '@/lib/utils';

export const Route = createFileRoute(
    '/(auth)/projects/$projectVersionId/re-inspections/distributionboards/',
)({
    component: DistributionBoardsPage,
    validateSearch: z.object({
        search: z.string().optional(),
    }),
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
            electricalProjectVersionId: params.projectVersionId,
        }));
    },
    pendingComponent: () => <Loading action="Verdeelinrichtingen aan het laden" />,
});

function DistributionBoardsPage() {
    const { t } = useTranslation('re-inspections');

    const params = Route.useParams();
    const search = Route.useSearch();
    const navigate = useNavigate();
    const searchInputRef = useRef<HTMLInputElement>(null);

    const [searchField, setSearchField] = useUrlSearchSync({ search: search.search, navigate: (newSearch) => {
        navigate({
            to: '.',
            search: prev => ({ ...prev, search: newSearch }),
            replace: true,
        });
    } });

    const [isSearchOpen, setIsSearchOpen] = useState(!!searchField);

    useEffect(() => {
        if (isSearchOpen && searchInputRef.current) {
            searchInputRef.current.focus();
        }
    }, [isSearchOpen]);

    function handleCreated(distributionBoardId: string) {
        navigate({
            to: '/projects/$projectVersionId/re-inspections/distributionboards/$distributionboardId/board/information',
            params: {
                ...params,
                distributionboardId: distributionBoardId,
            },
            search,
        });
    }

    return (
        <>
            <Page>
                <Section>
                    <Section.Header>
                        <Section.Title>
                            {t('installation.layout.navigation.distribution-boxes.label')}
                        </Section.Title>
                        <Section.Header.Actions>
                            {isSearchOpen
                                ? (
                                        <Input
                                            ref={searchInputRef}
                                            type="search"
                                            className="w-64"
                                            placeholder={t('actions.search.search', { ns: 'common' })}
                                            leftSlot={<Search size={20} />}
                                            value={searchField ?? ''}
                                            onChange={e => setSearchField(e.target.value)}
                                            onBlur={() => setIsSearchOpen(false)}
                                        />
                                    )
                                : (
                                        <Section.Header.Action
                                            icon={<Search size={20} />}
                                            onClick={() => setIsSearchOpen(true)}
                                        />
                                    )}
                            <DistributionBoardCreateModal
                                electricalProjectVersionId={params.projectVersionId}
                                onCreated={handleCreated}
                                trigger={(
                                    <Section.Header.Action
                                        variant="default"
                                        icon={<Plus size={20} />}
                                    />
                                )}
                            />
                        </Section.Header.Actions>
                    </Section.Header>
                    <Section.Content>
                        <DistributionBoardList />
                    </Section.Content>
                    <Section.Footer>
                        <FooterNavigation />
                    </Section.Footer>
                </Section>
            </Page>
        </>
    );
}

function DistributionBoardList() {
    const params = Route.useParams();
    const search = Route.useSearch();
    const { search: searchField } = search;

    const distributionBoards = useQuery(elektraAppQueryOptions.project.inspection.distributionBoard.distributionBoardListOptions({
        electricalProjectVersionId: params.projectVersionId,
    }));

    const filteredDistributionBoards = useMemo(() => {
        const boards = distributionBoards.data?.DistributionBoards ?? [];

        if (!searchField)
            return boards;

        return boards.filter(x => x.Identifier?.toLowerCase().includes(searchField.toLowerCase()));
    }, [distributionBoards.data, searchField]);

    return (
        <DistributionBoardTable
            electricalProjectVersionId={params.projectVersionId}
            distributionBoards={filteredDistributionBoards}
            isLoading={distributionBoards.isPending}
            search={search}
        />
    );
}
